import React, { createContext, useContext, useState } from "react";
import { Api } from "services/api/api.service";
import {
  CreditDeposit,
  DepositStatus,
  CreditDepositInput,
  CreditWithdrawal,
  WithdrawalStatus,
} from "model/TipCredit";

type TipsContextType = {
  tipAmount: number | "";
  setTipAmount: (amount: number | "") => void;
  deposits: CreditDeposit[];
  setDeposits: (deposits: any[]) => void;
  withdrawals: CreditWithdrawal[];
  setWithdrawals: (withdrawals: CreditWithdrawal[]) => void;
  modalOpen: boolean;
  openModal: (companyId?: number) => void;
  closeModal: () => void;
  submitTipDeposit: (companyId: number | undefined) => Promise<void>;
  fetchDeposits: (companyId: number) => Promise<void>;
  fetchWithdrawals: (companyId: number) => Promise<void>;
  updateDepositStatus: (
    depositId: number,
    companyId: number,
    status: DepositStatus
  ) => Promise<void>;
  updateWithdrawalStatus: (
    withdrawalId: number,
    status: WithdrawalStatus,
    companyId: number
  ) => Promise<void>;
};

const TipsContext = createContext<TipsContextType | undefined>(undefined);

export const TipsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tipAmount, setTipAmount] = useState<number | "">("");
  const [deposits, setDeposits] = useState<CreditDeposit[]>([]);
  const [withdrawals, setWithdrawals] = useState<CreditWithdrawal[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = async (companyId?: number) => {
    if (companyId) await fetchDeposits(companyId);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setTipAmount("");
  };

  const submitTipDeposit = async (companyId: number | undefined) => {
    if (!companyId || tipAmount === "") return;

    try {
      const deposit: CreditDepositInput = {
        companyId,
        amount: Number(tipAmount),
        reference: "Simflexity",
      };

      await Api().admin.addCompanyCreditDeposit(deposit);
      await fetchDeposits(companyId);
    } catch (err) {
      console.error("Error submitting tip deposit:", err);
    }
  };

  const fetchDeposits = async (companyId: number) => {
    try {
      const data = await Api().admin.getCompanyCreditDeposits(0, 20, companyId);
      setDeposits(data.deposits || []);
    } catch (error) {
      console.error("Failed to fetch deposits:", error);
    }
  };

  const updateDepositStatus = async (
    depositId: number,
    companyId: number,
    status: DepositStatus
  ) => {
    try {
      await Api().admin.updateCompanyCreditDepositStatus(
        depositId,
        companyId,
        status
      );

      setDeposits((prev) =>
        prev.map((deposit) =>
          deposit.id === depositId ? { ...deposit, status } : deposit
        )
      );
    } catch (err) {
      console.error("Failed to update deposit status:", err);
    }
  };

  const fetchWithdrawals = async (companyId: number) => {
    try {
      const data = await Api().admin.getCompanyCreditWithdrawals(
        0,
        20,
        companyId
      );
      setWithdrawals(data.withdrawals || []);
    } catch (err) {
      console.error("Failed to fetch withdrawals:", err);
    }
  };

  const updateWithdrawalStatus = async (
    withdrawalId: number,
    status: WithdrawalStatus,
    companyId: number
  ) => {
    try {
      await Api().admin.updateCompanyCreditWithdrawalStatus(
        withdrawalId,
        companyId,
        status
      );
      setWithdrawals((prev) =>
        prev.map((w) => (w.id === withdrawalId ? { ...w, status } : w))
      );
    } catch (err) {
      console.error("Failed to update withdrawal status:", err);
    }
  };

  return (
    <TipsContext.Provider
      value={{
        tipAmount,
        setTipAmount,
        deposits,
        setDeposits,
        setWithdrawals,
        withdrawals,
        modalOpen,
        openModal,
        closeModal,
        submitTipDeposit,
        fetchDeposits,
        updateDepositStatus,
        fetchWithdrawals,
        updateWithdrawalStatus,
      }}
    >
      {children}
    </TipsContext.Provider>
  );
};

export const useTips = (): TipsContextType => {
  const context = useContext(TipsContext);
  if (!context) throw new Error("useTips must be used within a TipsProvider");
  return context;
};
